import { ChevronLeft, ChevronRight, InfoIcon, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useUpdatePickupOrder } from '@/api/pickup-orders/queries/usePickupOrders'
import { PickupOrder, PickupOrderStatus } from '@/api/pickup-orders/types/PickupOrder'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import { BrandPreview } from '@/components/BrandPreview'
import { useConfirm } from '@/components/ConfirmDialog'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { getPickupOrderStatus } from '@/helpers/pkgUtils'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'

const MS_12H = 12 * 60 * 60 * 1000

interface Props {
  order: PickupOrder
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const isPickupWithin24Hours = (pickupDate: string): boolean => {
  const pickupDateTime = new Date(pickupDate.split('T')[0])
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  pickupDateTime.setHours(0, 0, 0, 0)
  return pickupDateTime <= now && pickupDateTime >= new Date(now.getTime() - MS_12H)
}

interface PickupOrderActionsMenuProps {
  order: PickupOrder
}

const PickupOrderActionsMenu = ({ order }: PickupOrderActionsMenuProps) => {
  const updateMutation = useUpdatePickupOrder()
  const { showSuccessToast, showErrorToast } = useToast()
  const { confirm, ConfirmDialog } = useConfirm()
  const isCancelable = order.status === PickupOrderStatus.Pending || order.status === PickupOrderStatus.FalseBarcode
  const within24h = isPickupWithin24Hours(order.pickupDate)
  const canCancel = isCancelable && !within24h

  const handleCancelPickupClick = async () => {
    if (!canCancel) return
    const confirmed = await confirm({
      title: 'ביטול איסוף',
      description: 'בטוחים שאתם רוצים לבטל את האיסוף?',
      helperText: (
        <p className='text-xs'>
          שימו לב: כאן מבטלים <span className='font-bold'>רק את האיסוף</span>. את ההזמנה מפריימארק צריך לבטל בנפרד.
        </p>
      ),
      confirmLabel: 'ביטול איסוף',
      cancelLabel: 'חזרה',
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await updateMutation.mutateAsync({ id: order.id, status: PickupOrderStatus.Cancelled })
      showSuccessToast('איסוף בוטל', 'האיסוף בוטל בהצלחה')
    } catch {
      showErrorToast('ביטול האיסוף נכשל', 'אנא נסה שנית או פנה לשירות הלקוחות')
    }
  }

  const cancelButton = (
    <Button
      icon={<XCircle className={cn('size-4', canCancel ? 'text-icon-primary' : 'text-icon-disabled')} />}
      variant='secondary'
      onClick={canCancel ? handleCancelPickupClick : undefined}
      disabled={!canCancel || updateMutation.isPending}
      className={cn(
        'gap-2 text-xs disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none',
        !canCancel && 'text-text-disabled cursor-default bg-[#F7F8F9] hover:shadow-none'
      )}
    >
      ביטול איסוף
    </Button>
  )

  return (
    <>
      {within24h ? (
        <AppTooltip triggerProps={{ asChild: true }} content='ניתן לבטל רק עד 24 שעות לפני מועד האיסוף' asChild side='top'>
          {cancelButton}
        </AppTooltip>
      ) : (
        cancelButton
      )}
      {ConfirmDialog}
    </>
  )
}

export const PickupOrderPreview = ({ order }: Props) => {
  const [_, setSearchParams] = useSearchParams()
  const [isReqMenuOpen, setIsReqMenuOpen] = useState(false)
  const status = getPickupOrderStatus(order)
  const warehouse = order.warehouse as PkgWarehouse
  const isFalseBarcode = order.status === PickupOrderStatus.FalseBarcode
  const isPendingBardcode = !order.barcodeImg
  const allowUploadBarcode = isFalseBarcode || isPendingBardcode
  const isCancelled = order.status === PickupOrderStatus.Cancelled
  const showMoreActions = !isCancelled

  const handleUploadBarcode = () => {
    setSearchParams(prev => {
      prev.set('pickupUploadOrderId', order.id)
      return prev
    })
  }

  return (
    <>
      <div className='border-border-default flex flex-col gap-4 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm'>
        {/* Header Section */}
        <div className='flex items-start justify-between'>
          {/* Brand and Warehouse */}
          <BrandPreview brand={'Primark'} warehouse={warehouse} />

          {/* Status Badge */}
          {status && (
            <div className='text-text-secondary bg-button-primary-selected-segment rounded-full px-3 py-1 text-center text-xs'>
              {status}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className='flex flex-wrap items-center gap-8 text-sm'>
          {/* Creation Date */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>תאריך הזמנה בפריימרק</p>
            <p className='font-medium'>{formatDate(order.createdAt)}</p>
          </div>

          {/* Order Number */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>מספר הזמנה</p>
            <p className='font-medium'>{order.orderNumber}</p>
          </div>

          {/* Pickup Date */}
          <div className='relative'>
            <AppTooltip content='התאריך שבחרתם בפריימרק לאיסוף ההזמנה'>
              <p className='text-text-secondary mb-1 flex items-center gap-1 text-xs'>
                איסוף מהסניף
                <InfoIcon className='size-2.5' />
              </p>
            </AppTooltip>
            <p className='font-medium'>{formatDate(order.pickupDate)}</p>
          </div>
        </div>
        {(allowUploadBarcode || showMoreActions) && <Divider />}

        {/* Bottom row: more actions + upload barcode */}
        <div className='flex items-center justify-end gap-2'>
          {showMoreActions && (
            <>
              <div
                className={cn(
                  'transform transition-all duration-300 ease-in-out',
                  isReqMenuOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
                )}
              >
                <PickupOrderActionsMenu order={order} />
              </div>
              <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                {isReqMenuOpen ? (
                  <ChevronLeft className='text-text-secondary size-4' />
                ) : (
                  <ChevronRight className='text-text-secondary size-4' />
                )}
                <span className='text-text-secondary text-xs'>פעולות נוספות</span>
              </Button>
            </>
          )}
          {allowUploadBarcode && (
            <Button type='button' onClick={handleUploadBarcode}>
              {isFalseBarcode ? 'העלאת ברקוד חדש' : 'העלאת ברקוד'}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
